/**
 * Command Center → Grade Visibility per Board. Each board gets one switch per
 * grade (Class 3-12) so content goes live to the right audience when ready.
 */
import { useState } from 'react';
import { ChevronDown, Layers } from 'lucide-react';
import { toast } from 'sonner';
import { Card } from '@/components/ui/card';
import { Switch } from '@/components/ui/switch';
import { apiPut } from '@/lib/api';
import { ALL_GRADES } from '@/lib/boardGrades';

function BoardGrades({ board, onUpdated }) {
  const [open, setOpen] = useState(!!board.visible);
  const hidden = new Set((board.hidden_classes || []).map(String));
  const shown = ALL_GRADES.filter(g => !hidden.has(g)).length;

  const toggle = async (grade) => {
    try {
      onUpdated(await apiPut(`/admin/boards/${board.board_id}/classes/${grade}/visibility`, {}));
    } catch (e) { toast.error(e?.message || 'Could not update that grade'); }
  };

  return (
    <div className="border-b border-slate-100 last:border-b-0" data-testid={`grade-board-${board.board_id}`}>
      <button onClick={() => setOpen(o => !o)} data-testid={`grade-board-toggle-${board.board_id}`}
        className="w-full flex items-center justify-between py-2.5 px-4 hover:bg-slate-50 transition-colors text-left">
        <span className="font-heading text-sm font-medium text-slate-700">
          {board.name}
          {!board.visible && <span className="ml-2 text-[10px] uppercase tracking-wider text-slate-400">board hidden</span>}
        </span>
        <span className="flex items-center gap-2 text-[11px] font-heading text-slate-500">
          {shown}/{ALL_GRADES.length} grades on
          <ChevronDown className={`w-4 h-4 transition-transform ${open ? 'rotate-180' : ''}`} />
        </span>
      </button>
      {open && (
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-x-6 gap-y-2 px-4 pb-4">
          {ALL_GRADES.map(g => (
            <label key={g} className="flex items-center justify-between gap-2 rounded-lg bg-slate-50 px-3 py-2" data-testid={`grade-row-${board.board_id}-${g}`}>
              <span className="font-body text-xs text-slate-700">Class {g}</span>
              <Switch checked={!hidden.has(g)} onCheckedChange={() => toggle(g)}
                data-testid={`grade-switch-${board.board_id}-${g}`} className="data-[state=checked]:bg-blue-500" />
            </label>
          ))}
        </div>
      )}
    </div>
  );
}

export function GradeVisibilityCard({ boards, setBoards }) {
  const [showAll, setShowAll] = useState(false);
  const onUpdated = (b) => setBoards(prev => prev.map(x => (x.board_id === b.board_id ? b : x)));
  const list = boards.filter(b => showAll || b.visible);
  return (
    <Card className="p-6 rounded-2xl border-slate-100" data-testid="grade-visibility-card">
      <div className="flex items-center justify-between gap-2 mb-2">
        <div className="flex items-center gap-2"><Layers className="w-5 h-5 text-blue-500" /><h3 className="font-heading text-base font-semibold text-slate-800">Grade Visibility per Board</h3></div>
        <label className="flex items-center gap-2 text-[11px] font-heading text-slate-500">
          Show hidden boards <Switch checked={showAll} onCheckedChange={setShowAll} data-testid="grade-show-hidden-boards" />
        </label>
      </div>
      <p className="text-xs text-slate-500 font-body mb-4">Turn a grade on when its content is ready — students only see the grades that are on when they pick their class. Students already in a grade are not affected.</p>
      <div className="border border-slate-100 rounded-xl overflow-hidden">
        {list.length ? list.map(b => <BoardGrades key={b.board_id} board={b} onUpdated={onUpdated} />)
          : <p className="p-4 text-xs text-slate-400 font-body">No boards are visible yet — turn one on above.</p>}
      </div>
    </Card>
  );
}

export default GradeVisibilityCard;
